import type {
  DashboardActivityAlertLevel,
  DashboardActivityRow,
  DashboardActivityTableSortColumn,
} from '../../interfaces/dashboardLeaderboardActivity';

/** Backend `sortBy` query values (Joi-valid). */
export type ActivityTableApiSortBy =
  | 'playerName'
  | 'dailyDate'
  | 'armFeels'
  | 'bodyFeels'
  | 'sleepQuality'
  | 'freshDate'
  | 'strengthAlert'
  | 'recoveryAlert'
  | 'postDate'
  | 'fatigueAlert'
  | 'type'
  | 'rpe'
  | 'count'
  | 'trainingSessions';

export const ACTIVITY_TABLE_SORT_UI_TO_API: Record<
  DashboardActivityTableSortColumn,
  ActivityTableApiSortBy
> = {
  playerName: 'playerName',
  dailyDate: 'dailyDate',
  armFeels: 'armFeels',
  bodyFeels: 'bodyFeels',
  sleepQuality: 'sleepQuality',
  freshDate: 'freshDate',
  strength: 'strengthAlert',
  recovery: 'recoveryAlert',
  postDate: 'postDate',
  fatigue: 'fatigueAlert',
  type: 'type',
  rpe: 'rpe',
  count: 'count',
  trainingSessions: 'trainingSessions',
};

/** Maps UI player group / position filter to activity-table `position` query (Joi: Pitchers | Catchers | Infielders | Outfielders). */
export function mapPlayersGroupToActivityTablePosition(playersGroup: string): string | undefined {
  const g = (playersGroup || '').trim().toLowerCase();
  if (!g) return undefined;
  if (g === 'pitcher' || g === 'pitchers') return 'Pitchers';
  if (g === 'catcher' || g === 'catchers') return 'Catchers';
  if (g === 'infield' || g === 'infielder' || g === 'infielders') return 'Infielders';
  if (g === 'outfield' || g === 'outfielder' || g === 'outfielders') return 'Outfielders';
  return undefined;
}

export function mapActivityTableApiAlert(value: unknown): DashboardActivityAlertLevel {
  if (value == null || typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (v === 'normal' || v === 'no alerts') return 'no alerts';
  if (v === 'medical') return 'medical';
  if (v === 'warning') return 'warning';
  if (v === 'watch') return 'watch';
  return null;
}

/** Normalizes GET /activity-table row into the dashboard UI row shape. */
export function mapActivityTableApiRow(raw: any): DashboardActivityRow {
  const daily = raw?.dailyCheckIn;
  const fresh = raw?.freshAlerts;
  const post = raw?.postAlerts;
  const training = raw?.training;

  return {
    playerId: typeof raw?.playerId === 'string' ? raw.playerId : '',
    playerName: typeof raw?.playerName === 'string' ? raw.playerName : '',
    dailyCheckIn:
      daily && typeof daily === 'object'
        ? {
            date: typeof daily.date === 'string' ? daily.date : null,
            armFeels: typeof daily.armFeels === 'number' ? daily.armFeels : null,
            bodyFeels: typeof daily.bodyFeels === 'number' ? daily.bodyFeels : null,
            sleepQuality: typeof daily.sleepQuality === 'number' ? daily.sleepQuality : null,
          }
        : null,
    freshAlerts:
      fresh && typeof fresh === 'object'
        ? {
            lastFreshDate: typeof fresh.date === 'string' ? fresh.date : null,
            strength: mapActivityTableApiAlert(fresh.strengthAlert),
            recovery: mapActivityTableApiAlert(fresh.recoveryAlert),
          }
        : null,
    postAlerts:
      post && typeof post === 'object'
        ? {
            lastPostDate: typeof post.date === 'string' ? post.date : null,
            fatigue: mapActivityTableApiAlert(post.fatigueAlert),
            activityType:
              post.type === null || post.type === undefined
                ? null
                : typeof post.type === 'string'
                  ? post.type
                  : null,
            rpe: typeof post.rpe === 'number' ? post.rpe : null,
            count: typeof post.count === 'number' ? post.count : null,
          }
        : null,
    trainingSessions: typeof training?.sessions === 'number' ? training.sessions : null,
    lastActivityAt: typeof raw?.lastActivityAt === 'string' ? raw.lastActivityAt : undefined,
  };
}
